// src/pages/home/Questionnaire.js
import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { questionModels } from "./questionnaire-models";

// ✅ Questionnaire.js lives in src/pages/home,
// questions-data.js lives in src/
import { questions } from "./questions-data";

/**
 * Toggle-friendly:
 * - mode defaults to "dog" so removing toggles won't break anything.
 * - mode filters questions by q.modes
 */
const Questionnaire = forwardRef(
  ({ dogName, activityType, location, mode = "dog" }, ref) => {
    const containerRef = useRef(null);
    const answersRef = useRef({});
    const requiredLabelsRef = useRef([]);

    useImperativeHandle(ref, () => ({
      getAnswers: () =>
        Object.entries(answersRef.current).map(([label, answer]) => ({
          question: label,
          answer,
        })),

      getMissingRequired: () => {
        return requiredLabelsRef.current.filter((label) => {
          const value = answersRef.current[label];

          if (Array.isArray(value)) return value.length === 0;
          return value === undefined || value === null || value === "";
        });
      },
    }));

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      container.innerHTML = "";
      answersRef.current = {};
      requiredLabelsRef.current = [];

      // 1) filter by MODE first (dog vs cat)
      const modeQuestions = questions.filter((q) => {
        if (!q.modes || q.modes.length === 0) return true; // (if you ever forget modes)
        return q.modes.includes(mode);
      });

      // 2) filter by activityType (DOG only)
      const applicableQuestions = modeQuestions.filter((q) => {
        // cats: ignore activities filter (cats don’t use Walk/Indoor/Outdoor)
        if (mode === "cat") return true;

        // dogs: apply activities filter if present
        if (!q.activities || q.activities.length === 0) return true;
        return q.activities.includes(activityType);
      });

      // required labels (ignore hidden)
      requiredLabelsRef.current = applicableQuestions
        .filter((q) => q.required && !q.hidden)
        .map((q) => q.label);

      // auto-fill hidden/system-generated questions
      applicableQuestions.forEach((q) => {
        if (!q.hidden) return;

        // your system questions
        if (q.id === "walkLocation" || q.id === "catLocation") {
          answersRef.current[q.label] = location || "";
        }
      });

      // render visible questions
      applicableQuestions.forEach((q) => {
        if (q.hidden) return;

        const renderFn = questionModels[q.type];
        if (!renderFn) return;

        const el = renderFn(q, (value) => {
          answersRef.current[q.label] = value;
        });

        container.appendChild(el);
      });
    }, [dogName, activityType, location, mode]);

    return (
      <div
        id={`questionnaire-container-${dogName}`}
        className="questionnaire-dropdown"
        ref={containerRef}
      />
    );
  }
);

export default Questionnaire;
